<template lang="pug">
.customer-fraud(v-loading="loading")

  template(v-if="fraudInformation")
    el-alert(
      v-if="isFraudulent"
      type="error"
      title="Cliente marcado como fraudulento."
      description="Novas assinaturas com cartão de crédito com dados correspondentes às informações abaixo são bloqueadas."
      :closable="false"
      show-icon
    )
    el-alert(
      v-else
      type="info"
      title="Cliente não possui informações de fraude registradas."
      :closable="false"
      show-icon
    )
  el-alert(
    v-else-if="!loading"
    type="warning"
    title="Falha ao carregar as informações de fraude. Recarregue a página ou tente novamente mais tarde."
    :closable="false"
    show-icon
  )

  .actions
    el-button(
      type="danger"
      :disabled="loading || !fraudInformation || isFraudulent"
      @click="confirmationDialogVisible = true"
    ) Marcar como fraudulento

  el-table(
    v-if="isFraudulent"
    border
    :data="fraudInformation"
    size="small"
  )
    el-table-column(label="Nome" min-width="140")
      template(slot-scope="scope") {{ scope.row.firstName }} {{ scope.row.lastName }}

    el-table-column(prop="cpf" label="CPF" width="130")

    el-table-column(label="Cartão" width="180")
      template(slot-scope="scope") {{ scope.row.cardBrand }} {{ scope.row.cardDisplayNumber }}

    el-table-column(prop="cardHolderName" label="Titular" min-width="140")

    el-table-column(label="Validade" width="80")
      template(slot-scope="scope") {{ formatExpiration(scope.row) }}

    el-table-column(label="Endereço" min-width="220")
      template(slot-scope="scope") {{ formatAddress(scope.row) }}

    el-table-column(prop="fingerprint" label="Fingerprint" min-width="120" show-overflow-tooltip)

    el-table-column(prop="ipAddress" label="IP" width="130")

  el-dialog(
    title="Marcar cliente como fraudulento"
    :visible.sync="confirmationDialogVisible"
    width="40%"
  )
    p.confirmation-text
      | Os dados de cartão de crédito, endereço e dispositivo deste cliente serão adicionados à base de fraudes,
      | e novas assinaturas com dados correspondentes serão bloqueadas.

    p.confirmation-text
      b Esta ação é irreversível e não pode ser desfeita.

    span(slot="footer")
      el-button(@click="confirmationDialogVisible = false" :disabled="marking") Cancelar
      el-button(type="danger" @click="markAsFraudulent" :loading="marking") Marcar como fraudulento

</template>
<script setup>
import { computed, onMounted, ref } from 'vue'

import CustomerService from '@/services/CustomerService'
import { useMessage } from '@/utils/Message'

const props = defineProps({
  customerId: { type: Number, required: true },
})

const message = useMessage()

const loading = ref(true)
const marking = ref(false)
const confirmationDialogVisible = ref(false)

// null while unknown (not loaded yet or failed to load)
const fraudInformation = ref(null)

const isFraudulent = computed(() => fraudInformation.value?.length > 0)

const formatExpiration = (information) =>
  `${String(information.cardExpirationMonth).padStart(2, '0')}/${information.cardExpirationYear}`

const formatAddress = (information) => {
  const complement = information.addressComplement ? ` - ${information.addressComplement}` : ''

  return `${information.addressPostalCode}, nº ${information.addressNumber}${complement} - ${information.addressCity}/${information.addressState}`
}

const load = async () => {
  loading.value = true

  try {
    fraudInformation.value = await CustomerService.getFraudulentInformation(props.customerId)
  } catch (e) {
    fraudInformation.value = null
  } finally {
    loading.value = false
  }
}

const markAsFraudulent = async () => {
  marking.value = true

  try {
    await CustomerService.markAsFraudulent(props.customerId)
  } catch (e) {
    message.error('Falha ao marcar cliente como fraudulento.')
    return
  } finally {
    marking.value = false
  }

  confirmationDialogVisible.value = false

  await load()

  // The backend only registers customers with a credit card and a shipping address,
  // and still answers 200 when nothing was inserted.
  if (isFraudulent.value) {
    message.success('Cliente marcado como fraudulento.')
  } else if (fraudInformation.value) {
    message.warning('Nenhuma informação foi registrada. O cliente precisa ter cartão de crédito e endereço cadastrados.')
  }
}

onMounted(load)
</script>
<style lang="scss" scoped>
.customer-fraud {
  .actions {
    text-align: center;
    margin: 16px 0;
  }

  .confirmation-text {
    word-break: normal;
  }
}
</style>
